import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Pane,         // Added
  Heading,      // Added
  Button,       // Added
  AddIcon,      // Added
  Table,        // Added
  IconButton,   // Added
  EditIcon,     // Added
  TrashIcon,    // Added
  Spinner,      // Added
  toaster,      // Added
  SideSheet,    // Added
  Paragraph,    // Added
  TextInputField, // Added
  SelectMenu,   // Added
  TagInput,     // Added
  Text,         // Added
  Dialog,       // Added for confirmation
  Alert,        // Added for confirmation dialog content
  InfoSignIcon,
} from 'evergreen-ui';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import GET_REGIONS from '../../graphql/queries/Settings';
import QUERY_GLOBAL_DATA from '../../graphql/queries/QueryGlobalData';
import {
  CREATE_REGION,
  UPDATE_REGION,
  DELETE_REGION,
} from '../../graphql/mutations/Settings';
import { ALL_COUNTRIES } from '../../utils/countries';
import { safeJSONParse } from '../../util';

const parseCountries = (countriesData) => {
  if (Array.isArray(countriesData)) {
    return countriesData;
  }
  if (!countriesData) return [];
  const parsed = safeJSONParse(countriesData);
  return Array.isArray(parsed) ? parsed : [];
};

// Helper function to get country name from code
const getCountryName = (code) => {
  const country = ALL_COUNTRIES.find(c => c.code === code);
  return country ? country.name : code;
};

// Helper function to get country names from codes
const getCountryNames = (codes) => {
  return codes.map(code => getCountryName(code));
};

function RegionManager() {
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const userid = useSelector((state) => state.user.value);
  
  const { data, loading, error, refetch } = useQuery(GET_REGIONS, {
    fetchPolicy: 'cache-and-network',
    onError: (err) => {
      console.error('Error fetching regions:', err);
      toaster.danger('Error loading regions: ' + err.message);
    }
  });

  // --- Mutations ---
  const [createRegion, { loading: createLoading }] = useMutation(CREATE_REGION, {
    refetchQueries: [
      { 
        query: QUERY_GLOBAL_DATA, 
        variables: { 
          user: parseInt(userid), 
          publisher: parseInt(currentpublisher.id) 
        } 
      }
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data.createRegion?.success) {
        toaster.success('Region created successfully.');
        refetch();
        handleCloseSheet();
      } else {
        // Use a generic error message as specific errors aren't available
        toaster.danger('Error: Failed to create region. Please check your input.');
      }
    },
    onError: (error) => {
      toaster.danger(`Error creating region: ${error.message}`);
    }
  });

  const [updateRegion, { loading: updateLoading }] = useMutation(UPDATE_REGION, {
    refetchQueries: [
      { 
        query: QUERY_GLOBAL_DATA, 
        variables: { 
          user: parseInt(userid), 
          publisher: parseInt(currentpublisher.id) 
        } 
      }
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data.updateRegion?.success) {
        toaster.success('Region updated successfully.');
        refetch();
        handleCloseSheet();
      } else {
        // Use a generic error message
        toaster.danger('Error: Failed to update region. Please check your input.');
      }
    },
    onError: (error) => {
      toaster.danger(`Error updating region: ${error.message}`);
    }
  });

  const [deleteRegion, { loading: deleteLoading }] = useMutation(DELETE_REGION, {
    refetchQueries: [
      { 
        query: QUERY_GLOBAL_DATA, 
        variables: { 
          user: parseInt(userid), 
          publisher: parseInt(currentpublisher.id) 
        } 
      }
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data.deleteRegion?.success) {
        toaster.success('Region deleted successfully.');
        refetch();
        handleCloseConfirm(); // Close confirm dialog on success
      } else {
        // Use a generic error message
        toaster.danger('Error: Failed to delete region.');
      }
    },
    onError: (error) => {
      toaster.danger(`Error deleting region: ${error.message}`);
    },
    // Update cache manually if needed, though refetch is simpler here
  });


  // --- State ---
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);
  const [regionFormState, setRegionFormState] = useState({ name: '', countries: [] });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [regionToDeleteId, setRegionToDeleteId] = useState(null);

  const regions = useMemo(() => data?.regions || [], [data]);
  const regionToDelete = useMemo(() => regions.find(r => r.id === regionToDeleteId), [regions, regionToDeleteId]);

  // --- Country Selection Logic ---
  const allAssignedCountryCodesInRegions = useMemo(() => {
    const allAssigned = new Set();
    regions.forEach(region => {
      const countries = parseCountries(region.countries);
      countries.forEach(code => allAssigned.add(code));
    });
    return allAssigned;
  }, [regions]);

  const assignedCountryCodes = useMemo(() => {
    const assignedExcludingEdit = new Set();
    regions.forEach(region => {
      if (editingRegion && region.id === editingRegion.id) return;
      const countries = parseCountries(region.countries);
      countries.forEach(code => assignedExcludingEdit.add(code));
    });
    return assignedExcludingEdit;
  }, [regions, editingRegion]);

  const selectableCountries = useMemo(() => {
    const currentRegionCountries = editingRegion ? parseCountries(editingRegion.countries) : [];
    return ALL_COUNTRIES.filter(country =>
      !assignedCountryCodes.has(country.code) || currentRegionCountries.includes(country.code)
    );
  }, [assignedCountryCodes, editingRegion]);

  // --- Rest of World Logic ---
  const restOfWorldCountries = useMemo(() => {
    return ALL_COUNTRIES.filter(country => !allAssignedCountryCodesInRegions.has(country.code));
  }, [allAssignedCountryCodesInRegions]);

  // --- Handlers ---
  const handleOpenAddSheet = () => {
    setEditingRegion(null);
    setRegionFormState({ name: '', countries: [] });
    setIsSheetOpen(true);
  };

  const handleOpenEditSheet = (region) => {
    setEditingRegion(region);
    setRegionFormState({
      name: region.name,
      countries: parseCountries(region.countries)
    });
    setIsSheetOpen(true);
  };

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    // Reset form after animation completes
    setTimeout(() => {
      setEditingRegion(null);
      setRegionFormState({ name: '', countries: [] });
    }, 300);
  };

  const handleFormChange = (key, value) => {
    setRegionFormState(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCountrySelect = (item) => {
    const code = item.code;
    setRegionFormState(prev => {
      const countries = [...prev.countries];
      const index = countries.indexOf(code);

      if (index > -1) {
        // Remove if already selected
        countries.splice(index, 1);
      } else {
        // Add if not selected
        countries.push(code);
      }

      return {
        ...prev,
        countries
      };
    });
  };

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!regionFormState.name.trim()) {
      toaster.danger("Region name is required.");
      return;
    }

    // Use the countries array directly - no JSON.stringify needed
    if (editingRegion) {
      // Update existing region
      await updateRegion({
        variables: {
          id: editingRegion.id,
          name: regionFormState.name,
          countries: regionFormState.countries
        }
      });
    } else {
      // Create new region
      await createRegion({
        variables: {
          name: regionFormState.name,
          countries: regionFormState.countries
        }
      });
    }
  }, [regionFormState, editingRegion, createRegion, updateRegion]);

  const handleOpenConfirm = (id) => {
    setRegionToDeleteId(id);
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setIsConfirmOpen(false);
    setRegionToDeleteId(null);
  };

  const handleDelete = useCallback(async () => {
    if (!regionToDeleteId) return;
    await deleteRegion({ variables: { id: regionToDeleteId } });
  }, [regionToDeleteId, deleteRegion]);


  // ... Loading/Error handling ...
   if (loading) {
     return <Spinner />;
   }

   if (error) {
     return (
       <Pane>
         <Alert
           intent="warning"
           title="Error loading regions"
           marginBottom={16}
         >
           Whoops! There was an error loading the region management feature. Please refresh your browser. If the problem persists, please contact support.
         </Alert>
       </Pane>
     );
   }

  return (
    <Pane marginTop={16} borderTop paddingTop={16}>

      {/* ... Add Region Button ... */}
       <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
         <Heading size={500}>Defined Regions</Heading>
         <Button iconBefore={AddIcon} onClick={handleOpenAddSheet}>Add Region</Button>
       </Pane>

      <Table>
        {/* ... Table Head ... */}
         <Table.Head>
           <Table.TextHeaderCell flexBasis={200} flexShrink={0} flexGrow={0}>Name</Table.TextHeaderCell>
           <Table.TextHeaderCell>Assigned Countries</Table.TextHeaderCell>
           <Table.TextHeaderCell flexBasis={120} flexShrink={0} flexGrow={0}>Actions</Table.TextHeaderCell>
         </Table.Head>
        <Table.Body>
          {/* ... No regions message ... */}
           {regions.length === 0 && (
             <Table.Row>
               <Table.TextCell colSpan={3}>No regions defined yet. The ability to create regions will be available soon.</Table.TextCell>
             </Table.Row>
           )}
          {regions.map((region) => {
            const regionCountries = parseCountries(region.countries);
            const countryNames = getCountryNames(regionCountries);

            return (
              <Table.Row key={region.id}>
                 {/* ... Name Cell ... */}
                 <Table.TextCell flexBasis={200} flexShrink={0} flexGrow={0}>{region.name}</Table.TextCell>
                 {/* ... Countries Cell (TagInput) ... */}
                 <Table.TextCell>
                   {regionCountries.length > 0 ? (
                     <TagInput
                       inputProps={{ disabled: true }}
                       width="100%"
                       values={countryNames}
                       disabled
                       tagProps={{ intent: 'neutral' }}
                     />
                   ) : (
                     <Text color="muted">None</Text>
                   )}
                 </Table.TextCell>
                <Table.Cell flexBasis={120} flexShrink={0} flexGrow={0}>
                  <IconButton icon={EditIcon} marginRight={8} appearance="minimal" onClick={() => handleOpenEditSheet(region)} />
                  <IconButton icon={TrashIcon} intent="danger" appearance="minimal" onClick={() => handleOpenConfirm(region.id)} />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      {/* Rest of World Section */}
      <Pane borderTop paddingTop={16}>
          <Heading size={500} marginBottom={8}>Rest of World</Heading>
          <Paragraph size={300} color="muted" marginBottom={8}>
            Countries not assigned to any specific region above will fall into the "Rest of World" category for royalty calculations.
          </Paragraph>
          {restOfWorldCountries.length > 0 ? (
              <TagInput
                inputProps={{ disabled: true }} // Readonly display
                width="100%"
                values={restOfWorldCountries.map(c => c.name)}
                disabled
                tagProps={{ intent: 'neutral' }}
              />
          ) : (
              <Text color="muted">All countries are assigned to regions.</Text>
          )}
      </Pane>

      {/* ... Add/Edit Side Sheet ... */}
      <SideSheet
         isShown={isSheetOpen}
         onCloseComplete={handleCloseSheet}
         preventBodyScrolling
         width={500}
       >
         {/* ... Sheet Header ... */}
           <Pane zIndex={1} flexShrink={0} elevation={4} backgroundColor="white">
             <Pane padding={16} borderBottom="muted">
               <Heading size={600}>{editingRegion ? 'Edit Region' : 'Add New Region'}</Heading>
             </Pane>
           </Pane>
         {/* ... Sheet Body (Form) ... */}
         <Pane flex="1" overflowY="scroll" backgroundColor="tint1" padding={16}>
           <form onSubmit={handleSubmit}>
             {/* ... Region Name ... */}
               <TextInputField
                 label="Region Name"
                 required
                 value={regionFormState.name}
                 onChange={e => handleFormChange('name', e.target.value)}
                 marginBottom={16}
                 description="A descriptive name for this sales region (e.g., 'North America', 'Europe')."
               />
             {/* ... Country SelectMenu ... */}
             <Pane marginBottom={16}>
                <Paragraph marginBottom={8}>Assign Countries</Paragraph>
                <SelectMenu
                   isMultiSelect
                   title="Select Countries"
                   options={selectableCountries.map(c => ({ label: `${c.name} (${c.code})`, value: c.code }))}
                   selected={regionFormState.countries}
                   onSelect={(item) => handleCountrySelect({ code: item.value })}
                   onDeselect={(item) => handleCountrySelect({ code: item.value })}
                   closeOnSelect={false}
                 >
                   <Button type="button">{regionFormState.countries.length > 0 ? `${regionFormState.countries.length} selected` : 'Select Countries...'}</Button>
                 </SelectMenu>
                 <Paragraph size={300} marginTop={4} color="muted">
                   Only countries not assigned to other regions are available.
                 </Paragraph>
             </Pane>
             {/* ... Selected Countries TagInput display ... */}
             {regionFormState.countries.length > 0 && (
                 <Pane marginBottom={24}>
                     <Paragraph size={300} marginBottom={4}>Selected:</Paragraph>
                     <TagInput
                         inputProps={{ disabled: true }}
                         width="100%"
                         values={regionFormState.countries.map(code => getCountryName(code))}
                         disabled
                         tagProps={{ intent: 'neutral' }}
                     />
                 </Pane>
             )}
             {/* ... Submit/Cancel Buttons ... */}
               <Button
                 type="submit"
                 appearance="primary"
                 intent="success"
                 isLoading={createLoading || updateLoading}
                 marginRight={8}
               >
                {editingRegion ? 'Save Changes' : 'Create Region'}
              </Button>
              <Button type="button" onClick={handleCloseSheet} disabled={createLoading || updateLoading}>
                Cancel
              </Button>
           </form>
         </Pane>
       </SideSheet>

      {/* Delete Confirmation Dialog */}
      <Dialog
        isShown={isConfirmOpen}
        title="Delete Region Confirmation"
        intent="danger"
        onCloseComplete={handleCloseConfirm} // Use onCloseComplete for state reset
        confirmLabel="Delete Region"
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        isConfirmLoading={deleteLoading}
      >
        <Alert intent="danger" title="Are you sure?">
          You are about to delete the region "{regionToDelete?.name || 'this region'}". This action cannot be undone.
          Countries assigned to this region ({parseCountries(regionToDelete?.countries).map(code => getCountryName(code)).join(', ') || 'none'}) will become part of the "Rest of World".
        </Alert>
      </Dialog>
    </Pane>
  );
}

export default RegionManager;
